<template>
  <div class="flex flex-col gap-6 proxima_nova">
    <div class="simple_navbar">
      <div class="flex flex-row items-center gap-2">
        <img width="32" alt="superagi_icon" :src="imagePath.superAgiIcon" />
        <span class="text_color text-[17px]">SuperAGI</span>
      </div>
    </div>
    <div class="flex flex-col items-center max-w-5xl mx-auto px-4 md:px-8 py-2 w-full" style="padding-top: 100px">
      <div class="flex flex-col gap-1 items-center">
        <span class="text-[30px] font-semibold text_color">Phone Numbers & Calling Routes Pricing</span>
        <span class="text-[18px] secondary_color">Pick a country to see pricing for numbers, inbound, and outbound routes.</span>
      </div>
      <CountryButtonDropdown v-if="!loadingCountries" :country-options="countryOptions" :default-country="defaultCountry" @country-change="handleCountryChange" />
      <div v-if="loadingCountries" class="flex justify-center items-center py-4">
        <span class="text_color text-sm">Loading countries...</span>
      </div>
      <div class="flex flex-col my-[72px] w-full">
        <span class="text-[20px] font-semibold text_color mb-4 text-center">Phone number cost</span>
        <SkeletonLoaders v-if="loading" :type="SKELETON_LOADER_TYPES.PRICING_CARDS" />
        <CallPricingCards v-else-if="!loading && phoneNumberInventory.length > 0" :phone-number-inventory="phoneNumberInventory" />
        <EmptyScreen v-else source="pricing_cards" text="Phone numbers for this country are currently unavailable" :padding="'py-0'" />
      </div>
      <div class="flex flex-col gap-4 w-full">
        <span class="text-[20px] font-semibold text_color text-center">Dialing cost</span>
        <template v-if="loading">
          <SkeletonLoaders :type="SKELETON_LOADER_TYPES.PRICING_TABLE" />
        </template>
        <template v-else>
          <TabComponent :tabs="callingTabs" :selected-tab="selectedTab" @tab-change="handleTabChange" :selected-background-color="'var(--white-opacity-8)'" />
          <div class="w-full">
            <template v-if="selectedTab === 'incoming'">
              <CustomTable v-if="incomingCallPricingData.length > 0" :headers="incomingCallPricingHeaders" :data="incomingCallPricingData" />
              <EmptyScreen v-else source="incoming_pricing" text="Incoming call pricing not available for this region yet" :padding="'py-8'" />
            </template>
            <template v-if="selectedTab === 'outgoing'">
              <CustomTable v-if="outgoingCallPricingData.length > 0" :headers="outgoingCallPricingHeaders" :data="outgoingCallPricingData" />
              <EmptyScreen v-else source="outgoing_pricing" text="No outgoing call pricing applicable" :padding="'py-8'" />
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { CALLING_TABS, INCOMING_CALL_PRICING_HEADERS, OUTGOING_CALL_PRICING_HEADERS } from '@/constants/countryConstants';
import CountryButtonDropdown from './CountryButtonDropdown.vue';
import CallPricingCards from './CallPricingCards.vue';
import imagePath from '@/javascript/imagePath';
import TabComponent from '@/components/manage_billing_components/TabComponent.vue';
import CustomTable from '@/components/custom/CustomTable.vue';
import DashboardService from '@/services/DashboardService';
import SkeletonLoaders from '@/components/custom/SkeletonLoaders.vue';
import { SKELETON_LOADER_TYPES } from '@/constants/skeletonLoaderConstants';
import EmptyScreen from '@/components/custom/EmptyScreen.vue';

const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode.length !== 2) return '🏳️';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

const transformCountryData = (apiCountries) => {
  if (!apiCountries || !Array.isArray(apiCountries)) return [];

  return apiCountries.map((country) => {
    const countryCode = country.country_code || country.code || '';
    return {
      code: countryCode,
      name: country.country || country.name || '',
      dialCode: countryCode,
      flag: getCountryFlag(countryCode),
    };
  });
};

const countryOptions = ref([]);
const defaultCountry = ref({
  code: 'US',
  name: 'United States',
  dialCode: '+1',
  flag: '🇺🇸',
});

const callingTabs = ref(CALLING_TABS);
const selectedTab = ref('outgoing');
const incomingCallPricingHeaders = ref(INCOMING_CALL_PRICING_HEADERS);
const outgoingCallPricingHeaders = ref(OUTGOING_CALL_PRICING_HEADERS);

const incomingCallPricingData = ref([]);
const outgoingCallPricingData = ref([]);
const phoneNumberInventory = ref([]);
const loading = ref(true);
const loadingCountries = ref(true);

const fetchCountryList = async () => {
  loadingCountries.value = true;
  try {
    const response = await DashboardService.getCountryList();
    if (response && response.countries) {
      const transformedCountries = transformCountryData(response.countries);
      countryOptions.value = transformedCountries;

      if (transformedCountries.length > 0) {
        const usCountry = transformedCountries.find((c) => c.code === 'US');
        defaultCountry.value = usCountry || {
          code: 'US',
          name: 'United States',
          dialCode: '+1',
          flag: '🇺🇸',
        };
        fetchCallPricing('US');
      }
    }
  } catch (error) {
    console.error('Error fetching country list:', error);
    // If API fails, set empty array - countries will come from API
    countryOptions.value = [];
    defaultCountry.value = {
      code: 'US',
      name: 'United States',
      dialCode: '+1',
      flag: '🇺🇸',
    };
    fetchCallPricing('US');
  } finally {
    loadingCountries.value = false;
  }
};

const fetchCallPricing = async (countryCode) => {
  loading.value = true;
  try {
    const response = await DashboardService.getCallPricing(countryCode);
    if (response) {
      phoneNumberInventory.value = (response.phone_number_inventory || []).filter((item) => item.type && item.type.trim() !== '');

      incomingCallPricingData.value = (response.inbound_pricing || []).map((item) => ({
        phoneNumberType: item.type || '',
        incomingRate: item.credits_per_min ? parseFloat(item.credits_per_min).toFixed(1) : '0.0',
      }));

      outgoingCallPricingData.value = (response.outbound_pricing || []).map((item) => {
        let description = item.description || '';
        if (description.startsWith('Programmable Outbound Minute -')) {
          description = description.replace('Programmable Outbound Minute -', '').trim();
        }
        return {
          routeDescription: description,
          originationPrefixes: (item.origination_prefixes || []).join(', ') || '-',
          destinationPrefixes: (item.destination_prefixes || []).join(', ') || '-',
          creditCharges: item.credits_per_min ? parseFloat(item.credits_per_min).toFixed(1) : '0.0',
        };
      });
    }
  } catch (error) {
    console.error('Error fetching call pricing:', error);
  } finally {
    loading.value = false;
  }
};

const handleCountryChange = (country) => {
  defaultCountry.value = country;
  fetchCallPricing(country.code);
};

const handleTabChange = (tab) => {
  selectedTab.value = tab;
};

onMounted(() => {
  fetchCountryList();
});
</script>

<style scoped>
.simple_navbar {
  height: 50px;
  background: #fafafa;
  padding: 0 24px;
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}
</style>
